import type { ModuleInstance } from "../models/ModuleInstance.js";

export interface ModuleInventoryState {
  readonly moduleInstances: ModuleInstance[];
}

/**
 * 보유 ModuleInstance 인벤토리 — 순수 로직 (단위 테스트 대상, 지시문 §6·§9·§10).
 * 골드 상자는 항상 새 인스턴스를 추가한다(중복 개념 없음 — 같은 definitionId를 여러 개 보유).
 * ⚠ 레벨 강화는 여기 없다 — 레벨은 moduleId별 플레이어 자산(PlayerModuleLevelController)이라
 *   인스턴스는 등급(rarity)·조각만 보유한다(2축 분리). 환생 시 clear()로 인스턴스만 소멸.
 */
export class ModuleInventory {
  private readonly instances = new Map<string, ModuleInstance>();
  private revisionCounter = 0;

  get revision(): number {
    return this.revisionCounter;
  }

  /** 새 인스턴스 추가 (항상 신규 — 같은 definitionId여도 별도 instanceId). */
  addInstance(instance: ModuleInstance): ModuleInstance {
    this.instances.set(instance.instanceId, instance);
    this.revisionCounter += 1;
    return instance;
  }

  getInstance(instanceId: string): ModuleInstance | undefined {
    return this.instances.get(instanceId);
  }

  hasInstance(instanceId: string): boolean {
    return this.instances.has(instanceId);
  }

  removeInstance(instanceId: string): boolean {
    const removed = this.instances.delete(instanceId);
    if (removed) this.revisionCounter += 1;
    return removed;
  }

  allInstances(): ModuleInstance[] {
    return [...this.instances.values()];
  }

  /** 보유 인스턴스 전부 제거 — 환생 시 보유 모듈 초기화에 사용. */
  clear(): void {
    if (this.instances.size === 0) return;
    this.instances.clear();
    this.revisionCounter += 1;
  }

  /** 특정 정의의 보유 인스턴스 목록. */
  instancesOf(definitionId: string): ModuleInstance[] {
    return this.allInstances().filter((i) => i.definitionId === definitionId);
  }

  countOf(definitionId: string): number {
    return this.instancesOf(definitionId).length;
  }

  /** 보유 중인 (중복 제거된) definitionId 목록. */
  ownedDefinitionIds(): string[] {
    return [...new Set(this.allInstances().map((i) => i.definitionId))];
  }

  toState(): ModuleInventoryState {
    return { moduleInstances: this.allInstances().map((i) => ({ ...i })) };
  }

  static fromState(state: ModuleInventoryState): ModuleInventory {
    const inv = new ModuleInventory();
    for (const inst of state.moduleInstances) {
      inv.instances.set(inst.instanceId, {
        instanceId: inst.instanceId,
        definitionId: inst.definitionId,
        acquiredAt: inst.acquiredAt ?? 0,
        locked: inst.locked ?? false,
        footprintId: inst.footprintId ?? "single",
        // shapeId 영속 — 누락(구 세이브) 시 straight_h 백필. v13 마이그레이션이 정의 조각으로 재정규화.
        shapeId: inst.shapeId ?? "straight_h",
        // 합성 등급 오버라이드 영속 — 누락 시 reload에 등급이 소실된다(미지정이면 키 생략).
        ...(inst.rarity ? { rarity: inst.rarity } : {}),
        // 융합 컴포넌트 영속 — 누락 시 reload에 융합 산출물이 소실된다(미지정이면 키 생략).
        ...(inst.fused ? { fused: inst.fused } : {}),
      });
    }
    return inv;
  }
}
